import posthog from "posthog-js";
import type { PostHogEventName } from "@jasperlabs/utils";

const PORTAL = "investor-portal";

/** Returns true only when both the flag is on AND a non-empty key is present. */
export function isPosthogEnabled(): boolean {
	return (
		process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "1" &&
		!!process.env.NEXT_PUBLIC_POSTHOG_KEY
	);
}

/**
 * posthog.reset() clears super-properties, so we must re-register
 * stable properties (like portal) after every reset.
 */
export function resetPosthog(): void {
	if (!isPosthogEnabled()) {
		return;
	}

	posthog.reset();
	posthog.register({ portal: PORTAL });
}

export type InvestorPersonProperties = {
	tenantId?: string;
	tenantName?: string;
	tenantCountry?: string;
	isAdviser?: boolean;
	adviserRegistrationCountry?: string;
};

export type TenantGroupProperties = {
	name?: string;
	country?: string;
	slug?: string;
};

export function registerTenantGroup(
	tenantId: string,
	properties: TenantGroupProperties,
): void {
	if (!isPosthogEnabled()) {
		return;
	}
	if (!tenantId) {
		return;
	}

	posthog.group("tenant", tenantId, properties);
}

/**
 * Emits a behavioural event to PostHog. Event names are typed (`PostHogEventName`)
 * for autocomplete and to turn typos into compile errors. The payload is scrubbed
 * by the existing `before_send` PII gate like every other event, so there is no
 * scrubbing here. No-ops when PostHog is disabled.
 */
export function track(
	event: PostHogEventName,
	properties?: Record<string, unknown>,
): void {
	if (!isPosthogEnabled()) {
		return;
	}

	posthog.capture(event, properties);
}

export { posthog };
